import { CSSProperties, FC, MouseEvent, ReactNode } from 'react'
import { composeClasses } from '@/utils/compose-classes'
import Spinner from '@/components/spinner'

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'terciary'
  | 'link'
  | 'cancel'

export interface ButtonProps {
  /**
   * If `true`, the button will be disabled.
   * @default false
   * */
  isDisabled?: boolean
  /**
   * If `true`, the button will be in a loading state.
   * @default false
   * */
  isLoading?: boolean
  /**
   * If `true`, the button will take up the full width of its container.
   * @default false
   * */
  fullWidth?: boolean
  /**
   * The `leftIcon?: ReactNode` prop is allowing the user to pass a ReactNode as the left icon.
   * */
  leftIcon?: ReactNode
  /**
   * The `rightIcon?: ReactNode` prop is allowing the user to pass a ReactNode as the right icon.
   * */
  rightIcon?: ReactNode
  /**
   * The `variant?: ButtonVariant` prop is allowing the user to pass a ButtonVariant as the variant.
   * @default 'primary'
   * */
  variant?: ButtonVariant
  /**
   * The `size?: 'xsmall' | 'small' | 'medium' prop is allowing the user to pass a 'xsmall' | 'small' | 'medium' as the size.
   * @default 'medium'
   * */
  size?: 'xsmall' | 'small' | 'medium'
  /**
   * The `style?: CSSProperties` prop is allowing the user to pass a CSSProperties as the style.
   * */
  style?: CSSProperties
  /**
   * The `className?: string` prop is allowing the user to pass a string as the className.
   * */
  className?: string
  /**
   * The `type?: 'button' | 'submit' | 'reset'` prop is allowing the user to pass a 'button' | 'submit' | 'reset' as the type.
   * @default 'button'
   * */
  type?: 'button' | 'submit' | 'reset'
  /**
   * The `children?: ReactNode` prop is allowing the user to pass a ReactNode as the children.
   * */
  children: ReactNode
  /**
   * The `onClick?: () => void` prop is allowing the user to pass a () => void as the onClick.
   * */
  onClick?: (event: MouseEvent) => void
  /**
   * The `dataTestId?: string` prop is allowing the user to pass a string as the dataTestId.
   * */
  dataTestId?: string
  /**
   * The `id?: string` prop allows passing a custom id to the button element
   * */
  id?: string
}

const assignedButtonVariant: { [key: string]: string } = {
  primary:
    'text-button-primary-text bg-button-primary hover:bg-button-primary-hover disabled:bg-button-disabled disabled:text-terciary active:bg-button-primary-pressed transition-all',
  secondary:
    'bg-button-dark text-white hover:brightness-90 disabled:bg-button-disabled disabled:text-terciary active:brightness-80',
  terciary:
    'text-button-secondary-text bg-button-secondary hover:bg-button-secondary-hover disabled:bg-button-disabled disabled:text-terciary active:bg-button-secondary-pressed',
  link: 'bg-transparent text-primary underline hover:brightness-150 disabled:bg-button-disabled disabled:text-terciary active:brightness-80',
  cancel:
    'bg-notif-red text-white hover:brightness-90 disabled:bg-button-disabled disabled:text-terciary active:brightness-75'
} as const

const assignedSizeVariants: { [key: string]: string } = {
  xsmall: 'px-6 py-2.5 max-h-[40px] text-body-bold-s',
  small: 'px-6 py-[14px] max-h-[48px] text-body-bold-m',
  medium: 'px-6 py-[14px] max-h-[52px] text-body-bold-l'
} as const

/* The code is defining a reusable Button. The Button accepts
various props such as `isDisabled`, `isLoading`, `fullWidth`, `leftIcon`, `rightIcon`, `variant`,
`size`, `style`, `className`, `type`, and `children`. */
export const Button: FC<ButtonProps> = (props) => {
  const {
    onClick,
    isDisabled = false,
    isLoading = false,
    children,
    leftIcon = null,
    rightIcon = null,
    fullWidth = false,
    type = 'button',
    className = '',
    style = {},
    variant = 'primary',
    size = 'medium',
    dataTestId,
    id
  } = props

  return (
    <button
      id={id}
      data-testid={dataTestId}
      onClick={onClick}
      type={type}
      disabled={isDisabled}
      role="button"
      className={composeClasses(
        assignedButtonVariant[variant],
        assignedSizeVariants[size],
        fullWidth && 'w-full',
        (isDisabled || isLoading) && 'pointer-events-none cursor-not-allowed',
        className,
        'cursor-pointer h-full flex flex-row items-center justify-center duration-200 ease-out transition-colors rounded-button gap-2 select-none relative whitespace-nowrap outline-none'
      )}
      style={style}
    >
      {isLoading ? (
        <Spinner
          color={variant}
          customBorderColor={isDisabled ? 'border-terciary' : undefined}
        />
      ) : (
        <ButtonContent leftIcon={leftIcon} rightIcon={rightIcon}>
          {children}
        </ButtonContent>
      )}
    </button>
  )
}

Button.displayName = 'Button'

type ButtonContentProps = Pick<
  ButtonProps,
  'leftIcon' | 'rightIcon' | 'children'
>

const ButtonContent = (props: ButtonContentProps) => {
  const { leftIcon, rightIcon, children } = props
  return (
    <>
      {leftIcon && <>{leftIcon}</>}
      {children}
      {rightIcon && <>{rightIcon}</>}
    </>
  )
}

export default Button
